import type { UserPrefs } from '@nagisot/shared';
import { applyTextScale } from './textscale.js';

const STYLE_ID = 'nagisot-prefs-style';
const DYSLEXIC_FONT_ID = 'nagisot-dyslexic-font';
const OVERRIDES_ID = 'nagisot-overrides';

// Per-site CSS overrides (AI/curated, from widget-config). Authored to be scoped
// under the nag-feat-* classes we set on <html>, so they only bite when the
// matching feature is active. Set once when the config loads.
let featureOverridesCss = '';

/**
 * Install per-site override CSS produced by the self-test "fix" flow. Scoped by
 * the nag-feat-* classes (see applyClientPrefs), so an override for, say,
 * contrast only applies while contrast is on.
 */
export function setFeatureOverrides(css: string | undefined | null): void {
  featureOverridesCss = (css || '').trim();
  let el = document.getElementById(OVERRIDES_ID) as HTMLStyleElement | null;
  if (!featureOverridesCss) { el?.remove(); return; }
  if (!el) {
    el = document.createElement('style');
    el.id = OVERRIDES_ID;
    document.head.appendChild(el);
  }
  el.textContent = featureOverridesCss;
}

/**
 * Load the OpenDyslexic webface once, on demand. Without this the dyslexia
 * toggle's `font-family: "OpenDyslexic"` silently falls back to Comic Sans on
 * every machine that doesn't have the font installed (i.e. almost all of them).
 */
function ensureDyslexicFont(): void {
  if (document.getElementById(DYSLEXIC_FONT_ID)) return;
  const style = document.createElement('style');
  style.id = DYSLEXIC_FONT_ID;
  const base = 'https://cdn.jsdelivr.net/npm/@fontsource/opendyslexic/files';
  style.textContent = `
    @font-face { font-family: 'OpenDyslexic'; font-style: normal; font-weight: 400;
      font-display: swap; src: url('${base}/opendyslexic-latin-400-normal.woff2') format('woff2'); }
    @font-face { font-family: 'OpenDyslexic'; font-style: normal; font-weight: 700;
      font-display: swap; src: url('${base}/opendyslexic-latin-700-normal.woff2') format('woff2'); }`;
  document.head.appendChild(style);
}

export function applyClientPrefs(prefs: UserPrefs): void {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = buildCss(prefs);

  if (prefs.dyslexiaFont) ensureDyslexicFont();

  document.documentElement.classList.toggle(
    'nagisot-stop-animations',
    !!prefs.stopAnimations,
  );
  // "Stop animations" also freezes animated GIFs + autoplay video (2.3.1/2.2.2)
  // via the loader's seizure guard, if present on the page.
  if (prefs.stopAnimations) {
    (window as unknown as { __nagisotSeizureGuard?: (all: boolean) => void }).__nagisotSeizureGuard?.(true);
  }

  // Feature flags on <html> so per-site overrides (setFeatureOverrides) can be
  // scoped to "only while this feature is active" (e.g. html.nag-feat-contrast).
  const root = document.documentElement.classList;
  root.toggle('nag-feat-font', (prefs.textSizePct ?? 100) !== 100);
  root.toggle('nag-feat-contrast', !!prefs.contrastMode && prefs.contrastMode !== 'off');
  root.toggle('nag-feat-dyslexia', !!prefs.dyslexiaFont);
  root.toggle('nag-feat-links', !!prefs.highlightLinks);
  root.toggle('nag-feat-cursor', !!(prefs.cursorSize && prefs.cursorSize > 1));
  root.toggle('nag-feat-mask', !!prefs.readingMask);
  root.toggle('nag-feat-anim', !!prefs.stopAnimations);

  // Robust text scaling: also resize px-based text the :root rule can't reach.
  applyTextScale(prefs.textSizePct ?? 100);
}

function buildCss(p: UserPrefs): string {
  const rules: string[] = [];

  // Text size is handled by the DOM-walk scaler (textscale.ts) so it works on
  // px-based sites too, not just rem/em — see applyTextScale().
  if (p.lineSpacing) {
    rules.push(`body, body * { line-height: ${p.lineSpacing} !important; }`);
  }
  if (p.letterSpacing) {
    rules.push(`body, body * { letter-spacing: ${p.letterSpacing}em !important; }`);
  }
  if (p.highlightLinks) {
    rules.push(
      `a, a * { text-decoration: underline !important; font-weight: 700 !important; outline: 1px dashed currentColor; }`,
    );
  }
  if (p.dyslexiaFont) {
    rules.push(`body, body * { font-family: "OpenDyslexic","Comic Sans MS",sans-serif !important; }`);
  }
  if (p.focusEnhance) {
    rules.push(
      `*:focus-visible { outline: 3px solid #fbbf24 !important; outline-offset: 2px !important; }`,
    );
  }
  if (p.stopAnimations) {
    rules.push(
      `.nagisot-stop-animations *, .nagisot-stop-animations *::before, .nagisot-stop-animations *::after {
        animation: none !important;
        transition: none !important;
        scroll-behavior: auto !important;
      }`,
    );
  }
  if (p.contrastMode && p.contrastMode !== 'off') {
    rules.push(contrastCss(p.contrastMode));
  }
  if (p.cursorSize && p.cursorSize > 1) {
    const size = p.cursorSize as 2 | 3 | 4;
    rules.push(`body, body * { cursor: ${cursorUri(size)} 0 0, auto !important; }`);
  }

  return rules.join('\n');
}

function contrastCss(mode: NonNullable<UserPrefs['contrastMode']>): string {
  switch (mode) {
    case 'dark':
      return `html { filter: invert(1) hue-rotate(180deg); } img, video, picture { filter: invert(1) hue-rotate(180deg); }`;
    case 'light':
      return `html { background: #ffffff !important; color: #000000 !important; }`;
    case 'high':
      return `body, body * { background-color: #000 !important; color: #fff !important; border-color: #fff !important; }
              a, a * { color: #ffeb3b !important; }`;
    case 'invert':
      return `html { filter: invert(1); } img, video { filter: invert(1); }`;
    default:
      return '';
  }
}

function cursorUri(size: 2 | 3 | 4): string {
  const px = 16 * size;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${px}' height='${px}' viewBox='0 0 24 24'><path d='M2 2l8 20 3-8 8-3z' fill='black' stroke='white' stroke-width='1.5'/></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}
